// Mixpanel is ~294KB. Statically importing it here (and this module is imported
// by AnalyticsContext, which lives in _app) shipped it in every page's initial
// bundle. Instead we dynamically import it so it lands in its own chunk, loaded
// off the hydration critical path (on idle, or on the first real event). A small
// facade preserves the synchronous `mixpanel.*` API used across the app by
// queueing calls until the library is ready.

type Mixpanel = typeof import("mixpanel-browser").default;

let mp: Mixpanel | null = null;
let loadPromise: Promise<Mixpanel> | null = null;
const queue: Array<(m: Mixpanel) => void> = [];

function ensureMixpanel(): Promise<Mixpanel> {
  if (mp) return Promise.resolve(mp);
  if (typeof window === "undefined") {
    return Promise.reject(new Error("mixpanel unavailable during SSR"));
  }
  if (!loadPromise) {
    loadPromise = import("mixpanel-browser").then((mod) => {
      const inst = mod.default;
      inst.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN!);
      mp = inst;
      queue.splice(0).forEach((fn) => {
        try {
          fn(inst);
        } catch (e) {
          console.error("mixpanel queued call failed", e);
        }
      });
      return inst;
    });
  }
  return loadPromise;
}

// Run once mixpanel is ready; queue until then. Does NOT trigger the load
// itself, so buffered identify/reset/etc. flush when the idle preload (or a
// track event) actually loads the library.
function enqueue(fn: (m: Mixpanel) => void) {
  if (mp) {
    try {
      fn(mp);
    } catch (e) {
      console.error("mixpanel call failed", e);
    }
    return;
  }
  queue.push(fn);
}

// Fetch mixpanel when the browser is idle — keeps it off the hydration path
// while still loading it shortly after page load. Call once from a provider.
export function preloadMixpanelWhenIdle() {
  if (typeof window === "undefined") return;
  const load = () => {
    ensureMixpanel().catch(() => {});
  };
  if ("requestIdleCallback" in window) {
    (
      window as unknown as {
        requestIdleCallback: (cb: () => void, o?: object) => void;
      }
    ).requestIdleCallback(load, { timeout: 4000 });
  } else {
    setTimeout(load, 2000);
  }
}

export async function track(event: string, properties: Record<string, any>) {
  if (typeof window === "undefined") return;
  const m = await ensureMixpanel().catch(() => null);
  if (!m) return;
  return new Promise<void>((resolve) =>
    m.track(event, properties, () => resolve()),
  );
}

// Facade preserving the synchronous surface the app calls on the mixpanel
// object. `get_property` returns undefined until the library loads (only used
// for the deviceId cookie, which is set via onReady after load).
export const mixpanel = {
  track: (event: string, props?: Record<string, any>) =>
    enqueue((m) => m.track(event, props)),
  identify: (id: string) => enqueue((m) => m.identify(id)),
  reset: () => enqueue((m) => m.reset()),
  people: {
    set: (props: Record<string, any>) => enqueue((m) => m.people.set(props)),
  },
  get_property: (key: string) => (mp ? mp.get_property(key) : undefined),
  onReady: (cb: (m: Mixpanel) => void) => enqueue(cb),
};
